import type { CheckboxFieldProps } from "react-aria-components";
import { CheckboxButton, CheckboxField } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const checkbox = tv({
  base: "group relative flex items-center gap-3 text-sm transition",
  slots: {
    wrapper: `
      flex size-4 shrink-0 items-center justify-center rounded-sm border
      border-border transition-colors duration-500
      group-data-[selected=true]:bg-primary
      group-data-[indeterminate=true]:bg-primary
    `,
    icon: "",
    label: "text-text-primary",
  },
  variants: {
    checkboxPosition: {
      right: {
        base: "flex-row-reverse",
        label: "mr-auto",
      },
      left: {},
    },
    isDisabled: {
      true: { base: "cursor-not-allowed", label: "text-text-tertiary" },
    },
    color: {
      primary: {},
    },
    size: {
      sm: {},
      md: {},
      lg: {
        wrapper: "size-5",
      },
    },
  },
  defaultVariants: { color: "primary", checkbox: "left" },
});

function stopIfInsideLink(event: React.SyntheticEvent<HTMLElement>) {
  if ((event.target as Element).closest("a[href]")) {
    event.stopPropagation();
  }
}

export default function CustomCheckbox({
  size,
  color,
  children,
  checkboxPosition,
  className,
  classNames,
  onClick,
  onPointerDown,
  ...props
}: Omit<
  CheckboxFieldProps,
  "children" | "className" | "onClick" | "onPointerDown"
> & {
  readonly children?: React.ReactNode | React.ReactNode[];
  readonly className?: string;
  readonly classNames?: { label?: string };
  readonly onClick?: React.MouseEventHandler<HTMLElement>;
  readonly onPointerDown?: React.PointerEventHandler<HTMLElement>;
} & VariantProps<typeof checkbox>) {
  const { base, label, wrapper } = checkbox({
    size,
    color,
    isDisabled: props.isDisabled,
    checkboxPosition,
  });

  return (
    // rac strips dom onClick from CheckboxField and CheckboxButton, so the
    // click guard lives on this display:contents wrapper; onPointerDown stays
    // on the button because rac's press system stops pointer propagation at
    // the label before it would reach the wrapper. the field div is also
    // display:contents so the button label stays the visual root, matching
    // the DOM the deprecated single-element Checkbox produced
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click-propagation guard around a nested interactive widget, not an interactive element itself
    <span
      className="contents"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      <CheckboxField slot="selection" {...props} className="contents">
        <CheckboxButton
          className={base({ className })}
          onPointerDown={(event) => {
            event.stopPropagation();
            onPointerDown?.(event);
          }}
        >
          {({ isIndeterminate }) => (
            <>
              <div className={wrapper()}>
                {isIndeterminate ? (
                  <svg
                    aria-hidden="true"
                    width="70%"
                    height="70%"
                    viewBox="0 0 10 2"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1H9"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    aria-hidden="true"
                    width="70%"
                    height="70%"
                    viewBox="0 0 10 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="group-data-[selected=true]:animate-checkbox-in"
                      d="M1 4L3.66667 7L9 1"
                      stroke="black"
                      strokeDasharray="100"
                      strokeDashoffset="100"
                    />
                  </svg>
                )}
              </div>
              {children && (
                // a link inside the label must open without toggling the
                // box: browsers already skip label activation for it, but
                // rac's press handling on the label would still fire
                // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- propagation guard for nested links, not an interactive element itself
                <span
                  className={label({ className: classNames?.label })}
                  onPointerDown={stopIfInsideLink}
                  onClick={stopIfInsideLink}
                >
                  {children}
                </span>
              )}
            </>
          )}
        </CheckboxButton>
      </CheckboxField>
    </span>
  );
}
